import { useEffect, useId, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { cn } from "../lib/cn";
import { intlLocale } from "../i18n/config";

export interface DateFieldProps {
  label: string;
  value?: string; // ISO yyyy-mm-dd
  onChange?: (iso: string) => void;
  hint?: string;
  error?: string;
  required?: boolean;
  hideLabel?: boolean;
  id?: string;
}

// Monday-first weekday + month i18n key suffixes; resolved via t() at render so
// labels follow the active locale.
const WEEKDAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const MONTH_KEYS = [
  "january", "february", "march", "april", "may", "june",
  "july", "august", "september", "october", "november", "december",
];

function toISO(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}
function fromISO(s?: string): Date | null {
  if (!s) return null;
  const [y, m, d] = s.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

/**
 * A text-style date input that opens an accessible calendar popup. Built in
 *-house (no date library) so it inherits the design system exactly. Closes on
 * Escape / outside-click; the popup honours prefers-reduced-motion.
 */
export function DateField({
  label,
  value,
  onChange,
  hint,
  error,
  required,
  hideLabel,
  id,
}: DateFieldProps) {
  const { t } = useTranslation();
  const auto = useId();
  const fieldId = id ?? auto;
  const hintId = hint ? `${fieldId}-hint` : undefined;
  const errId = error ? `${fieldId}-err` : undefined;
  const reduce = useReducedMotion();

  const selected = fromISO(value);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<Date>(selected ?? new Date());
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const grid = useMemo(() => {
    const first = new Date(view.getFullYear(), view.getMonth(), 1);
    const startOffset = (first.getDay() + 6) % 7; // Monday-first
    const days: (Date | null)[] = [];
    for (let i = 0; i < startOffset; i++) days.push(null);
    const total = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate();
    for (let d = 1; d <= total; d++)
      days.push(new Date(view.getFullYear(), view.getMonth(), d));
    return days;
  }, [view]);

  const today = toISO(new Date());

  return (
    <div className="w-full" ref={rootRef}>
      <label
        htmlFor={fieldId}
        className={cn(
          "mb-1.5 block font-display text-sm font-bold text-body",
          hideLabel && "sr-only",
        )}
      >
        {label}
        {required && <span className="ml-1 text-ember">*</span>}
      </label>

      <div className="relative">
        <button
          type="button"
          id={fieldId}
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-invalid={error ? true : undefined}
          aria-describedby={cn(hintId, errId) || undefined}
          className={cn(
            "flex h-12 w-full items-center justify-between rounded-md bg-surface px-4 text-left text-base",
            "ring-1 ring-inset ring-line-strong shadow-xs transition-shadow duration-fast",
            "focus:ring-2 focus:ring-inset focus:ring-volt focus-visible:outline-none",
            error && "ring-2 ring-danger focus:ring-danger",
          )}
        >
          <span className={cn(!selected && "text-body-faint")}>
            {selected
              ? selected.toLocaleDateString(intlLocale(), {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })
              : t("dateField.selectDate")}
          </span>
          <svg viewBox="0 0 24 24" className="h-5 w-5 text-body-muted" fill="none" aria-hidden>
            <rect x="3" y="4.5" width="18" height="16" rx="3" stroke="currentColor" strokeWidth="2" />
            <path d="M3 9h18M8 2.5v4M16 2.5v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        <AnimatePresence>
          {open && (
            <motion.div
              role="dialog"
              aria-label={t("dateField.calendarAria", { label })}
              initial={reduce ? false : { opacity: 0, y: -6, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={reduce ? { opacity: 0 } : { opacity: 0, y: -6, scale: 0.98 }}
              transition={{ duration: 0.16, ease: [0.22, 0.61, 0.36, 1] }}
              className="absolute left-0 z-30 mt-2 w-72 rounded-lg bg-surface p-4 shadow-lg ring-1 ring-line"
            >
              <div className="mb-3 flex items-center justify-between">
                <button
                  type="button"
                  aria-label={t("dateField.previousMonth")}
                  onClick={() => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1))}
                  className="grid h-8 w-8 place-items-center rounded-sm text-body-muted hover:bg-surface-sunken"
                >
                  ‹
                </button>
                <span className="font-display text-sm font-bold">
                  {t(`dateField.months.${MONTH_KEYS[view.getMonth()]}`)} {view.getFullYear()}
                </span>
                <button
                  type="button"
                  aria-label={t("dateField.nextMonth")}
                  onClick={() => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1))}
                  className="grid h-8 w-8 place-items-center rounded-sm text-body-muted hover:bg-surface-sunken"
                >
                  ›
                </button>
              </div>

              <div className="mb-1 grid grid-cols-7 gap-1 text-center">
                {WEEKDAY_KEYS.map((w) => (
                  <span key={w} className="text-xs font-bold uppercase text-body-faint">
                    {t(`dateField.weekdays.${w}`)}
                  </span>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-1">
                {grid.map((d, i) => {
                  if (!d) return <span key={`x${i}`} />;
                  const iso = toISO(d);
                  const isSel = value === iso;
                  const isToday = iso === today;
                  return (
                    <button
                      key={iso}
                      type="button"
                      aria-pressed={isSel}
                      onClick={() => {
                        onChange?.(iso);
                        setOpen(false);
                      }}
                      className={cn(
                        "grid h-9 place-items-center rounded-sm text-sm transition-colors",
                        isSel
                          ? "bg-grad-coral font-bold text-white shadow-volt"
                          : "hover:bg-surface-sunken",
                        !isSel && isToday && "ring-1 ring-inset ring-ember font-bold text-ember",
                      )}
                    >
                      {d.getDate()}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {error ? (
        <p id={errId} className="mt-1.5 text-sm font-medium text-danger">
          {error}
        </p>
      ) : (
        hint && (
          <p id={hintId} className="mt-1.5 text-sm text-body-muted">
            {hint}
          </p>
        )
      )}
    </div>
  );
}
